<template>
  <div
    ref="mapContainer"
    style="width: 100%; height: 300px; border-radius: 1rem"
  ></div>
</template>

<script setup>
import { ref, onMounted, watch, inject } from "vue";
import { Loader } from "@googlemaps/js-api-loader";

const $env = inject("$env");
const props = defineProps({
  locations: {
    type: Array,
    required: true,
  },
});

const mapContainer = ref(null);
let map = null;
let markers = [];

const loader = new Loader({
  apiKey: $env.VUE_APP_GOOGLE_MAPS_API_KEY,
  version: "weekly",
});

function zoomToLocation(position) {
  if (!map) return;

  map.setZoom(12);
  map.setCenter(position);
}

function updateMarkersVisibility(currentZoom) {
  const labels = document.querySelectorAll(".marker-text");
  labels.forEach((label) => {
    label.classList.toggle("zoom-visible", currentZoom >= 6);
  });
}

function createCustomMarker(location) {
  const markerElement = document.createElement("div");
  markerElement.className = "custom-marker";
  const pinColor = location.hasOpenRequest ? "#ff4444" : "#4CAF50";
  markerElement.innerHTML = `
   <span class="marker-text">${location.name}</span>
    <div class="marker-pin">
      <svg class="pin-shape" xmlns="http://www.w3.org/2000/svg" viewBox="0 0 24 24" width="32" height="32">
        <path fill="${pinColor}" d="M12 2C8.13 2 5 5.13 5 9c0 5.25 7 13 7 13s7-7.75 7-13c0-3.87-3.13-7-7-7z"/>
      </svg>
    </div>
   
  `;
  return markerElement;
}

async function addMarkers() {
  if (!map || !props.locations.length) return;

  markers.forEach((marker) => (marker.map = null));
  markers = [];

  const { AdvancedMarkerElement } = await loader.importLibrary("marker");

  props.locations.forEach((loc) => {
    const markerElement = createCustomMarker(loc);
    const position = { lat: loc.lat, lng: loc.lng };
    const marker = new AdvancedMarkerElement({
      position,
      map,
      content: markerElement,
    });
    marker.addListener("click", () => {
      zoomToLocation(position);
    });

    markers.push(marker);
  });

  if (props.locations.length > 0) {
    map.setCenter({
      lat: props.locations[0].lat,
      lng: props.locations[0].lng,
    });
  }
}

onMounted(async () => {
  try {
    const { Map } = await loader.importLibrary("maps");

    const mapOptions = {
      center: props.locations.length
        ? { lat: props.locations[0].lat, lng: props.locations[0].lng }
        : { lat: 39, lng: -98 }, // US center
      zoom: 3,
      mapId: "b402a9ce4c185aac",
      disableDefaultUI: true,
      zoomControl: false,
      mapTypeControl: false,
      streetViewControl: false,
      fullscreenControl: false,
      keyboardShortcuts: false,
    };

    map = new Map(mapContainer.value, mapOptions);

    map.addListener("zoom_changed", () => {
      const currentZoom = map.getZoom();
      updateMarkersVisibility(currentZoom);
    });

    if (props.locations.length) {
      await addMarkers();
      updateMarkersVisibility(map.getZoom());
    }
  } catch (e) {
    console.error("Error loading Google Maps:", e);
  }
});

watch(
  () => props.locations,
  async (newLocations) => {
    if (newLocations.length && map) {
      await addMarkers();
    }
  },
  { deep: true }
);
</script>
<style lang="css">
.custom-marker {
  cursor: pointer;
  display: flex;
  flex-direction: column;
  align-items: center;
  position: relative;
  gap: 4px;
}

.marker-text {
  position: absolute;
  bottom: 100%;
  left: 50%;
  transform: translateX(-50%);
  opacity: 0;
  visibility: hidden;
  color: black;
  font-size: 12px;
  font-weight: 600;
  background: white;
  padding: 4px 8px;
  border-radius: 4px;
  white-space: nowrap;
  box-shadow: 0 2px 4px rgba(0, 0, 0, 0.2);
  transition: all 0.2s ease-in-out;
  pointer-events: none;
  margin-bottom: 8px;
}

.marker-text::after {
  content: "";
  position: absolute;
  top: 100%;
  left: 50%;
  transform: translateX(-50%);
  border-left: 6px solid transparent;
  border-right: 6px solid transparent;
  border-top: 6px solid white;
}

.custom-marker:hover .marker-text,
.marker-text.zoom-visible {
  opacity: 1;
  visibility: visible;
}

.marker-pin {
  transform-origin: bottom center;
  transition: transform 0.2s ease-out;
}

.custom-marker:hover .marker-pin {
  transform: scale(1.1);
}
</style>
